import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(data && data.authenticated === false ? false : data);
    } catch (error) {
      // No auto-login in production — user must sign up or log in.
      setUser(false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // API is a module-level constant, axios is an import — stable references

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Auto-refresh token on 401 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url && !originalRequest.url.includes('/auth/')) {
          originalRequest._retry = true;
          try {
            await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
            return axios(originalRequest);
          } catch (refreshError) {
            setUser(false);
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty — interceptor should be set up once on mount

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const register = async (email, password, brandName, companyName) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/signup`,
        { email, password, brand_name: brandName, company_name: companyName || brandName },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const googleLogin = async (email, name, googleId) => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/google`,
        { email, name, google_id: googleId },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const demoLogin = async () => {
    try {
      const { data } = await axios.post(`${API}/api/auth/demo-login`, {}, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      if (data && data.authenticated === false) {
        setUser(false);
        return null;
      }
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    googleLogin,
    demoLogin,
    refreshUser,
    logout,
    checkAuth,
    isAuthenticated: !!user && user !== false
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
